// FILE: src/components/branding/PMPixelLogo.js
//
// PMPixelLogo — the single, presentational entry point for rendering the
// PM Pixel wordmark anywhere in the app.  Call sites just ask for a size:
//
//     <PMPixelLogo />                         // default: medium wordmark
//     <PMPixelLogo size="small" />
//     <PMPixelLogo size="large" decorative={false} />
//
// Theme resolution mirrors CoachAvatar / AchievementBadge: the asset is served
// from the active theme's branding folder (PHASE 1: the /themes/pm-pixel/brand/
// constant below).  The component's *public API* is already theme-shaped, so
// Phase 2 (white-label) swaps the base path + variant map for values read from
// the theme manifest with NO change to any call site.  Swapping the logo image,
// or theming it, is therefore a single asset/manifest update — never a code edit
// at the places that render it.
//
// No Redux.  Functional component, hooks only.

import React from 'react';
import mcss from './PMPixelLogo.module.css';

// ─── Phase-1 theme stand-in ──────────────────────────────────────────────────
// In Phase 2 this comes from the active theme manifest (theme.branding.basePath).
// Keeping it as a constant for now lets us ship branding without the theme layer.
// Points at the established brand asset folder (alongside logo.jpg), matching
// the repo's existing /themes/pm-pixel/<category>/ convention.
const BRANDING_BASE_PATH = '/themes/pm-pixel/brand/';

// Default variant used when an unknown / unmapped token is requested.
const FALLBACK_VARIANT = 'wordmark';

// variant token -> descriptor.  `file` resolves against BRANDING_BASE_PATH;
// `alt` is the human-readable label used when the logo is NOT decorative.
// New variants (e.g. 'mark' for the mascot glyph alone, or 'stacked' for a
// two-line lockup) drop in here later with no call-site changes.
const VARIANT_REGISTRY = {
  wordmark: {
    file: 'pm-pixel-logo.png',
    alt: 'PM Pixel',
  },
};

// Size token -> CSS Module class.  Actual widths (and the responsive mobile
// shrink) live in PMPixelLogo.module.css so sizing is never duplicated inline.
// Canonical desktop widths: small ≈ 120px, medium ≈ 160px, large ≈ 200px.
const SIZE_CLASS = {
  small: mcss.small,
  medium: mcss.medium,
  large: mcss.large,
};

/**
 * @param {string}  [size]       'small' | 'medium' | 'large' (default 'medium').
 *                               Drives the rendered width via the CSS Module;
 *                               height is derived from the asset's aspect ratio.
 * @param {string}  [variant]    One of VARIANT_REGISTRY (default 'wordmark').
 *                               Reserved for future lockups; unknown tokens fall
 *                               back to the wordmark.
 * @param {boolean} [decorative] When true (default), the image is aria-hidden
 *                               with empty alt — it's ornamental chrome.  Set
 *                               false to expose the brand name to assistive tech.
 * @param {string}  [loading]    <img> loading attr. Defaults to 'eager' since the
 *                               logo is above-the-fold header chrome.
 * @param {string}  [className]  Optional extra class on the wrapper.
 */
export default function PMPixelLogo({
  size = 'medium',
  variant = FALLBACK_VARIANT,
  decorative = true,
  loading = 'eager',
  className = '',
}) {
  // If the asset 404s or fails to decode, hide it gracefully rather than show a
  // broken-image icon. (Same defensive pattern as CoachAvatar.)
  const [failed, setFailed] = React.useState(false);

  // ── Branding enabled? (Phase 2: theme.branding.enabled) ──
  // A white-label theme can suppress the wordmark entirely; this component then
  // renders nothing app-wide with no per-call-site changes.
  const brandingEnabled = true;
  if (!brandingEnabled) return null;

  // ── Resolve variant with fallback ──
  let token = variant;
  let descriptor = VARIANT_REGISTRY[token];
  if (!descriptor) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        `[PMPixelLogo] Unknown variant "${variant}". Falling back to "${FALLBACK_VARIANT}".`
      );
    }
    token = FALLBACK_VARIANT;
    descriptor = VARIANT_REGISTRY[FALLBACK_VARIANT];
  }

  // Defense in depth: missing descriptor or a failed load -> render nothing.
  if (!descriptor || failed) return null;

  const sizeClass = SIZE_CLASS[size] || SIZE_CLASS.medium;
  const wrapperClass = [mcss.logo, sizeClass, className].filter(Boolean).join(' ');

  return (
    <span
      className={wrapperClass}
      data-testid="pm-pixel-logo"
      data-variant={token}
      data-size={size}
    >
      <img
        className={mcss.img}
        src={BRANDING_BASE_PATH + descriptor.file}
        loading={loading}
        decoding="async"
        alt={decorative ? '' : descriptor.alt}
        aria-hidden={decorative ? 'true' : undefined}
        draggable="false"
        onError={() => setFailed(true)}
      />
    </span>
  );
}
